import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { email, maxLength, minLength, phone, required, username } from '../utils/formValidators';
import type { Validator } from '../types/validator.type';

interface InputField<Value = unknown> {
  value: Value;
  required?: boolean;
  optional?: boolean;
  minLength?: number | { length: number, error: string };
  maxLength?: number | { length: number, error: string };
  phone?: boolean;
  email?: boolean;
  username?: boolean;
  validators?: Validator[];
}

type Form = Record<string, string | number | boolean | undefined | InputField>;

type FieldValue<T> = T extends InputField<infer Value> ? Value : T;

type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

type FormValues<F extends Form> = {
  [key in keyof F]: FieldValue<F[key]>;
};

type FieldState<T> = {
  value: FieldValue<T>;
  initialValue: FieldValue<T>;
  validators: Validator[];
  invalid: boolean;
  error?: string;
};

type FormState<F extends Form> = {
  [key in keyof F]: FieldState<F[key]>;
};

export type Field<T> = FieldState<T> & {
  validate(value?: FieldValue<T>): boolean;
  setError(error?: string): void;
  update(value: FieldValue<T>): void;
  clear(): void;
  addValidator(validator: Validator): void;
  removeValidator(validator: string | Validator): void;
  onChange(value: FieldValue<T>): void;
  onBlur(): void;
};

type Fields<F extends Form> = {
  [key in keyof F]: Expand<Field<F[key]>>;
};

function initializeState<T extends Form>(initialState: T) {

  const values = {} as FormState<T>;

  for (const key in initialState) {

    const field = initialState[key];

    const isField = field !== null && typeof field === 'object';

    const value = isField 
      ? field.value as any
      : field;

    const validators: Validator[] = [];

    if (isField) {
      if (field.required || !field.optional) validators.push(required);
      if (field.phone) validators.push(phone);
      if (field.email) validators.push(email);
      if (field.username) validators.push(username);
      if (field.validators) validators.push(...field.validators);
      if (field.minLength !== undefined) {
        if (typeof field.minLength === 'number') {
          validators.push(minLength(field.minLength));
        } else {
          validators.push(minLength(field.minLength.length, field.minLength.error));
        }
      }
      if (field.maxLength !== undefined) {
        if (typeof field.maxLength === 'number') {
          validators.push(maxLength(field.maxLength));
        } else {
          validators.push(maxLength(field.maxLength.length, field.maxLength.error));
        }
      }
    } else {
      validators.push(required);
    }

    values[key] = {
      value,
      initialValue: value,
      invalid: false,
      error: undefined,
      validators
    };
  }

  return values;
}

export function useForm<T extends Form>(initialState: T, deps: unknown[] = []) {

  const [state, setState] = useState(() => initializeState(initialState));

  useEffect(() => {
    setState(() => initializeState(initialState));
  }, deps);

  const value = useMemo(() => {

    const values = {} as FormValues<T>;

    for (const key in state) {
      values[key] = state[key].value as any;
    }

    return values as unknown as Expand<FormValues<T>>;
  }, [state]);

  const fields = useMemo(() => {

    const fields = {} as Fields<T>;
    
    for (const key in state) {
      fields[key] = createField(key, state, setState);
    }
    
    return fields;
  }, [state]);

  const validate = useCallback(() => {

    let valid = true;
    const newState = {} as FormState<T>;

    for (const key in state) {
      
      const field = state[key];

      const failedValidator = field.validators.find(validator => {
        if (validator.key === 'required') return !validator.validate(field.value);
        if (!field.value) return false;
        return !validator.validate(field.value);
      });

      if (failedValidator) {
        valid = false;
      }

      newState[key] = { 
        ...state[key], 
        invalid: failedValidator !== undefined, 
        error: failedValidator?.error 
      };
    }

    setState(newState);

    return valid;
  }, [state]);

  const clear = useCallback(() => {

    setState((state) => {

      const newState = {} as FormState<T>;

      for (const key in state) {
        newState[key] = {
          ...state[key],
          value: state[key].initialValue,
          invalid: false,
          error: undefined
        };
      }

      return newState;
    });
  }, []);

  return {
    ...fields,
    value,
    validate,
    clear
  };
}


function createField<T extends Form, K extends keyof T>(
  key: K, 
  state: FormState<T>, 
  setState: Dispatch<SetStateAction<FormState<T>>>
): Field<T[K]> {

  function onChange(value: unknown) {
    setState(state => ({ 
      ...state, 
      [key]: { ...state[key], value }
    }));
  }

  function onBlur() {
    validate();
  }

  function addValidator(validator: Validator) {
    setState(state => ({
      ...state,
      [key]: { 
        ...state[key], 
        validators: [
          ...state[key].validators.filter(item => item.key !== validator.key), 
          validator
        ]
      }
    }));
  }

  function removeValidator(validator: string | Validator) {
    setState(state => ({
      ...state,
      [key]: { 
        ...state[key], 
        validators: state[key].validators.filter(item => 
          typeof validator === 'string' 
            ? item.key !== validator 
            : item.key !== validator.key
        )
      }
    }));
  }

  function validate(value = state[key].value) {

    const field = state[key];

    const failedValidator = field.validators.find(validator => {
      if (validator.key === 'required') return !validator.validate(value);
      if (!value) return false;
      return !validator.validate(value);
    });

    setState({ 
      ...state, 
      [key]: { 
        ...field,
        value,
        invalid: failedValidator !== undefined,
        error: failedValidator?.error
      }
    });

    return failedValidator === undefined;
  }

  function clear() {
    setState((state) => ({
      ...state,
      [key]: {
        ...state[key],
        value: state[key].initialValue,
        invalid: false,
        error: undefined
      }
    }));
  }

  function setError(error?: string) {
    setState((state) => ({
      ...state,
      [key]: {
        ...state[key],
        invalid: error !== undefined,
        error
      }
    }));
  }
  
  return {
    value: state[key].value,
    initialValue: state[key].initialValue,
    validators: state[key].validators,
    invalid: state[key].invalid,
    error: state[key].error,
    validate,
    setError,
    addValidator,
    removeValidator,
    update: onChange,
    clear,
    onChange,
    onBlur
  };
}
